import { Vec3 } from 'playcanvas';

import { Events } from './events';
import { getSceneCameraData } from './scene-camera';
import type { SceneCameraPose } from './scene-camera-anim-track';
import type { HelperPoint } from './helper-point';

type OrbitDirection = 'cw' | 'ccw';

type EasePreset = 'linear' | 'easeIn' | 'easeOut' | 'easeInOut';

type OrbitParams = {
    center: Vec3;
    radius: number;
    elevation: number;
    revolutions: number;
    direction: OrbitDirection;
    startFrame: number;
    endFrame: number;
    pointsPerRevolution: number;
    ease: EasePreset;
};

const defaultOrbitParams = (): OrbitParams => ({
    center: new Vec3(0, 0, 0),
    radius: 5,
    elevation: 15,
    revolutions: 1,
    direction: 'ccw',
    startFrame: 0,
    endFrame: 150,
    pointsPerRevolution: 12,
    ease: 'linear'
});

/**
 * Apply easing function to a normalized t value [0, 1].
 */
const applyEase = (t: number, ease: EasePreset): number => {
    switch (ease) {
        case 'easeIn':
            return t * t;
        case 'easeOut':
            return 1 - (1 - t) * (1 - t);
        case 'easeInOut':
            return t < 0.5 ? 2 * t * t : 1 - 2 * (1 - t) * (1 - t);
        default:
            return t;
    }
};

/**
 * Generate orbit keyframes analytically.
 * Returns poses in world space (center is world space).
 */
const generateOrbitPoses = (params: OrbitParams): SceneCameraPose[] => {
    const {
        center, radius, elevation, revolutions,
        direction, startFrame, endFrame,
        pointsPerRevolution, ease
    } = params;

    const totalPoints = Math.max(2, Math.round(revolutions * pointsPerRevolution));
    const totalAngle = revolutions * Math.PI * 2;
    const sign = direction === 'cw' ? -1 : 1;
    const elevationRad = (elevation * Math.PI) / 180;
    const cosElev = Math.cos(elevationRad);
    const sinElev = Math.sin(elevationRad);

    const poses: SceneCameraPose[] = [];

    for (let i = 0; i <= totalPoints; i++) {
        const tLinear = i / totalPoints;
        const tEased = applyEase(tLinear, ease);
        const angle = tEased * totalAngle * sign;
        const frame = Math.round(startFrame + tLinear * (endFrame - startFrame));

        const x = center.x + radius * cosElev * Math.sin(angle);
        const y = center.y + radius * sinElev;
        const z = center.z + radius * cosElev * Math.cos(angle);

        poses.push({
            name: `orbit_${i}`,
            frame,
            position: new Vec3(x, y, z),
            target: center.clone()
        });
    }

    return poses;
};

/**
 * Apply orbit keyframes to an existing scene camera, replacing its current track.
 * Positions are stored in parent-local space (matching SceneCameraAnimTrack convention).
 */
const applyOrbitToSceneCamera = (
    helper: HelperPoint,
    events: Events,
    params: OrbitParams
): boolean => {
    const data = getSceneCameraData(helper as any);
    if (!data) return false;

    const poses = generateOrbitPoses(params);

    // Convert world-space poses to parent-local space
    const parent = helper.entity.parent;
    if (parent) {
        const invWorld = parent.getWorldTransform().clone().invert();
        for (const p of poses) {
            invWorld.transformPoint(p.position, p.position);
            invWorld.transformPoint(p.target, p.target);
        }
    }

    // Position the camera entity at the first pose
    if (poses.length > 0) {
        helper.entity.setLocalPosition(poses[0].position);
    }

    data.animTrack.loadPoses(poses);

    // Extend timeline if needed
    const endFrame = params.endFrame;
    const currentFrames = events.invoke('timeline.frames') as number;
    if (typeof currentFrames === 'number' && endFrame > currentFrames) {
        events.fire('timeline.setFrames', endFrame);
    }

    events.fire('track.keysLoaded');
    return true;
};

export {
    generateOrbitPoses,
    applyOrbitToSceneCamera,
    defaultOrbitParams
};

export type {
    OrbitParams,
    OrbitDirection,
    EasePreset
};
